/**
 * Briques visuelles du design v2.
 *
 * Le signe distinctif du prototype validé : chaque écran s'ouvre sur un
 * BANDEAU D'ENCRE (`--ink`, #0b1220) à coins bas arrondis, qui porte le
 * chiffre-clé de l'écran et ses actions principales. Le reste de la page est
 * une colonne de cartes blanches sur fond gris, sans titre de page — le
 * bandeau EST le titre.
 *
 * Styles inline sur les variables CSS plutôt que des classes Tailwind : une
 * valeur ne peut pas dériver d'un écran à l'autre puisqu'elle est définie ici
 * une seule fois.
 */
import { createContext, useCallback, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';

// ── Jetons ──────────────────────────────────────────────────────────────────

export const CARD: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 16,
  background: 'var(--card)',
  boxShadow: '0 1px 2px rgba(16,24,40,.05)',
};

export const INPUT: CSSProperties = {
  minHeight: 54,
  padding: '0 15px',
  borderRadius: 13,
  border: '1px solid var(--border)',
  background: 'var(--input)',
  color: 'var(--text)',
  fontSize: 15,
  width: '100%',
  boxSizing: 'border-box',
};

export const LABEL: CSSProperties = { fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' };

/** Micro-titre en capitales — sur fond clair. Sur l'encre, voir InkHeader. */
export const KICKER: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: 'var(--faint)',
};

export const SCREEN: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  animation: 'alFade .18s',
  color: 'var(--text)',
  fontFamily: "-apple-system, 'SF Pro Text', 'Segoe UI', Roboto, system-ui, sans-serif",
};

export const SCROLL: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '16px 20px calc(var(--safe-b) + 108px)',
};

/** Conservé pour compatibilité — le design v2 n'affiche plus de titre de page. */
export const H1: CSSProperties = { fontSize: 26, fontWeight: 700, letterSpacing: '-.02em' };

/** Bouton d'un contrôle segmenté : la sélection est un fond blanc surélevé. */
export function seg(on: boolean): CSSProperties {
  return {
    flex: 1,
    minHeight: 44,
    border: 'none',
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? 'var(--card)' : 'transparent',
    color: on ? 'var(--text)' : 'var(--muted)',
    boxShadow: on ? '0 1px 3px rgba(16,24,40,.12)' : 'none',
  };
}

/** Même contrôle, posé sur le bandeau d'encre : l'inactif doit rester lisible. */
export function segInk(on: boolean): CSSProperties {
  return {
    flex: 1,
    minHeight: 42,
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? '#fff' : 'transparent',
    color: on ? 'var(--ink)' : '#c8cfd9',
  };
}

// ── Bandeau d'encre ─────────────────────────────────────────────────────────

export interface InkAction {
  label: string;
  icon?: string;
  onClick: () => void;
  /** Couleur de fond : var(--ok) pour démarrer, var(--err) pour arrêter… */
  bg?: string;
  /** Bouton carré secondaire de 56 px, posé à droite de l'action principale. */
  square?: boolean;
  ariaLabel?: string;
}

/**
 * Le bandeau d'encre.
 *
 * `kicker`  — micro-titre en capitales (« Indemnités · septembre »)
 * `metric`  — le chiffre-clé, 40 px, tabulaire
 * `note`    — la phrase qui explique d'où vient le chiffre
 * `second`  — un second chiffre, plus petit, aligné à droite du premier
 * `right`   — élément en haut à droite (avatar, badge d'état)
 * `actions` — la rangée de boutons sous le chiffre
 * `live`    — la barre d'état (point coloré + libellé + valeur)
 */
export function InkHeader({
  kicker, metric, note, second, right, actions, live, children, metricSize = 40, pt = 18,
}: {
  kicker: ReactNode;
  metric?: ReactNode;
  note?: ReactNode;
  second?: { value: ReactNode; label: string };
  right?: ReactNode;
  actions?: ReactNode;
  live?: { color: string; label: ReactNode; value?: ReactNode; pulse?: boolean };
  children?: ReactNode;
  metricSize?: number;
  pt?: number;
}) {
  return (
    <div
      style={{
        flex: 'none',
        background: 'var(--ink)',
        padding: `calc(var(--safe-t) + ${pt}px) 20px 20px`,
        borderRadius: '0 0 24px 24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
          <span
            style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.11em',
              textTransform: 'uppercase', color: 'var(--ink-t)',
            }}
          >
            {kicker}
          </span>
          {metric !== undefined && (
            <span
              style={{
                fontSize: metricSize, fontWeight: 700, letterSpacing: '-.03em', color: '#fff',
                fontVariantNumeric: 'tabular-nums', lineHeight: 1.05,
              }}
            >
              {metric}
            </span>
          )}
          {note && <span style={{ fontSize: 12.5, color: 'var(--ink-t)', lineHeight: 1.4 }}>{note}</span>}
        </div>
        {second && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', flex: 'none', alignSelf: 'flex-end' }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {second.value}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink-t)' }}>{second.label}</span>
          </div>
        )}
        {right}
      </div>

      {actions && <div style={{ marginTop: 18, display: 'flex', gap: 9 }}>{actions}</div>}

      {live && (
        <div
          style={{
            marginTop: 14, display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', borderRadius: 12, background: 'var(--ink-2)',
          }}
        >
          <span
            style={{
              width: 8, height: 8, flex: 'none', borderRadius: 99, background: live.color,
              animation: live.pulse ? 'alPulse 1.8s infinite' : undefined,
            }}
          />
          <span style={{ flex: 1, fontSize: 12.5, color: '#e4e8ef', lineHeight: 1.35 }}>{live.label}</span>
          {live.value !== undefined && (
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
              {live.value}
            </span>
          )}
        </div>
      )}

      {children}
    </div>
  );
}

/** Bouton principal du bandeau : pleine largeur, 56 px, fond coloré. */
export function InkButton({ label, icon, onClick, bg = 'var(--ok)' }: InkAction) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, minHeight: 56, border: 'none', borderRadius: 14, background: bg,
        color: '#fff', fontSize: 15.5, fontWeight: 700, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
      }}
    >
      {icon && <i className={icon} style={{ fontSize: 20 }}></i>}
      {label}
    </button>
  );
}

/** Bouton carré 56 px du bandeau — la seconde action, sur fond ink-2. */
export function InkSquare({ icon, onClick, ariaLabel, caption }: {
  icon: string; onClick: () => void; ariaLabel: string; caption?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        flex: 'none', width: 56, minHeight: 56, border: '1px solid var(--ink-line)',
        borderRadius: 14, background: 'var(--ink-2)', color: '#fff', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
      }}
    >
      <i className={icon} style={{ fontSize: caption ? 20 : 24 }}></i>
      {caption && (
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.04em', color: 'var(--ink-t)' }}>{caption}</span>
      )}
    </button>
  );
}

/** Pastille d'initiales, en haut à droite du bandeau. */
export function InkAvatar({ initials, onClick, size = 44 }: { initials: string; onClick?: () => void; size?: number }) {
  return (
    <button
      onClick={onClick}
      aria-label="Profil"
      style={{
        width: size, height: size, flex: 'none', border: 'none', borderRadius: 99,
        background: 'var(--ink-2)', color: '#fff', fontSize: size > 50 ? 19 : 14,
        fontWeight: 700, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {initials}
    </button>
  );
}

/** Petit pavé chiffré sur fond ink-2, par rangée de deux ou trois. */
export function InkTile({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div style={{ flex: 1, padding: '12px 14px', borderRadius: 12, background: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <span style={{ fontSize: 11, color: 'var(--ink-t)' }}>{label}</span>
    </div>
  );
}

/** Étiquette d'état posée à droite du kicker. */
export function InkChip({ label, bg = 'rgba(255,255,255,.14)', fg = '#fff' }: { label: string; bg?: string; fg?: string }) {
  return (
    <span
      style={{
        flex: 'none', padding: '4px 10px', borderRadius: 99, background: bg, color: fg,
        fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
      }}
    >
      {label}
    </span>
  );
}

// ── Pavés chiffrés sur fond clair ───────────────────────────────────────────

export interface StatDef {
  /** Conservé pour compatibilité : ignoré par le rendu v2. */
  icon?: string;
  color?: string;
  tint?: string;
  value: string | number;
  /** Devient le micro-titre en capitales du pavé. */
  label: string;
  /** Ligne d'explication sous le chiffre. */
  note?: string;
  onClick?: () => void;
}

/** Grille de deux colonnes — micro-titre, chiffre, note. Pas d'icône en v2. */
export function StatGrid({ stats }: { stats: StatDef[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {stats.map((s) => {
        const body = (
          <>
            <span style={KICKER}>{s.label}</span>
            <span style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.025em', lineHeight: 1 }}>
              {s.value}
            </span>
            {s.note && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{s.note}</span>}
          </>
        );
        const style: CSSProperties = {
          ...CARD, padding: 15, display: 'flex', flexDirection: 'column', gap: 9,
          textAlign: 'left', color: 'var(--text)',
        };
        return s.onClick
          ? <button key={s.label} onClick={s.onClick} style={{ ...style, cursor: 'pointer' }}>{body}</button>
          : <div key={s.label} style={style}>{body}</div>;
      })}
    </div>
  );
}

// ── Contrôles ───────────────────────────────────────────────────────────────

/** Interrupteur : piste de 48×28, pastille blanche de 24. */
export function Toggle({ on, onChange, label, note }: {
  on: boolean; onChange: (v: boolean) => void; label: string; note?: string;
}) {
  return (
    <button
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: 0, border: 'none',
        background: 'transparent', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', minHeight: 48,
      }}
    >
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>{label}</span>
        {note && <span style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>{note}</span>}
      </span>
      <span
        style={{
          width: 48, height: 28, flex: 'none', borderRadius: 99,
          background: on ? 'var(--brand)' : 'var(--border)', padding: 2,
          display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
          boxSizing: 'border-box', marginTop: 2,
        }}
      >
        <span style={{ width: 24, height: 24, borderRadius: 99, background: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,.28)' }} />
      </span>
    </button>
  );
}

/** Rangée cliquable d'une carte-liste : icône, libellé, valeur, chevron. */
export function RowButton({ icon, label, value, note, onClick, first, danger }: {
  icon?: string; label: string; value?: ReactNode; note?: string;
  onClick: () => void; first?: boolean; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '15px 16px',
        border: 'none', borderTop: first ? 'none' : '1px solid var(--border)',
        background: 'transparent', color: danger ? 'var(--err)' : 'var(--text)',
        cursor: 'pointer', minHeight: 58, textAlign: 'left',
      }}
    >
      {icon && <i className={icon} style={{ fontSize: 19, color: danger ? 'var(--err)' : 'var(--muted)', flex: 'none' }}></i>}
      <span style={{ flex: note ? 1 : 'none', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: 600 }}>{label}</span>
        {note && <span style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.35 }}>{note}</span>}
      </span>
      {value !== undefined && (
        <span
          style={{
            flex: 1, minWidth: 0, textAlign: 'right', fontSize: 13, color: 'var(--faint)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
      )}
      <i className="ri-arrow-right-s-line" style={{ fontSize: 19, color: 'var(--faint)', flex: 'none' }}></i>
    </button>
  );
}

/** Carte sans rembourrage, destinée à empiler des RowButton. */
export function ListCard({ children }: { children: ReactNode }) {
  return <div style={{ ...CARD, overflow: 'hidden' }}>{children}</div>;
}

/** Carte titrée : micro-titre en capitales puis contenu. */
export function Panel({ title, icon, iconColor, action, children, border }: {
  title?: string; icon?: string; iconColor?: string; action?: ReactNode; children: ReactNode; border?: string;
}) {
  return (
    <div
      style={{
        ...CARD,
        border: `1px solid ${border || 'var(--border)'}`,
        padding: 16, display: 'flex', flexDirection: 'column', gap: 13,
      }}
    >
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {icon && <i className={icon} style={{ fontSize: 18, color: iconColor || 'var(--brand)' }}></i>}
          <span style={{ flex: 1, fontSize: 14.5, fontWeight: 700 }}>{title}</span>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/** Ligne d'un tableau de calcul : libellé à gauche, valeur tabulaire à droite. */
export function CalcRow({ k, v, strong, rule }: { k: ReactNode; v: ReactNode; strong?: boolean; rule?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
        paddingTop: rule ? 12 : 0, borderTop: rule ? '1px solid var(--border)' : 'none',
      }}
    >
      <span style={{ fontSize: strong ? 14 : 12.5, color: strong ? 'var(--text)' : 'var(--muted)', fontWeight: strong ? 700 : 400 }}>{k}</span>
      <span style={{ fontSize: strong ? 15 : 12.5, fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}

/** Encart d'information gris — le ton explicatif du prototype. */
export function Hint({ icon = 'ri-information-line', children }: { icon?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 14, background: 'var(--sk)' }}>
      <i className={icon} style={{ fontSize: 17, color: 'var(--muted)', flex: 'none' }}></i>
      <span style={{ flex: 1, fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

// ── Feuille du bas ──────────────────────────────────────────────────────────

export function Sheet({ open, onClose, title, children }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(11,18,32,.55)', animation: 'alFade .2s', zIndex: 40 }}
      />
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 50,
          background: 'var(--card)', borderRadius: '22px 22px 0 0',
          padding: '16px 20px calc(var(--safe-b) + 22px)',
          animation: 'alUp .3s cubic-bezier(.22,.9,.3,1)',
          display: 'flex', flexDirection: 'column', gap: 13, maxHeight: '92%', overflow: 'auto',
          color: 'var(--text)',
        }}
      >
        <div style={{ width: 38, height: 4, borderRadius: 99, background: 'var(--border)', alignSelf: 'center' }} />
        <span style={{ fontSize: 18, fontWeight: 700 }}>{title}</span>
        {children}
      </div>
    </>
  );
}

export function EmptyState({ icon, title, note, action }: {
  icon: string; title: string; note: string; action?: ReactNode;
}) {
  return (
    <div style={{ padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 13, textAlign: 'center' }}>
      <div
        style={{
          width: 62, height: 62, borderRadius: 18, background: 'var(--sk)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <i className={icon} style={{ fontSize: 30, color: 'var(--faint)' }}></i>
      </div>
      <span style={{ fontSize: 17, fontWeight: 700 }}>{title}</span>
      <span style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>{note}</span>
      {action}
    </div>
  );
}

// ── Toasts ──────────────────────────────────────────────────────────────────

type Toast = { id: number; msg: string; kind: 'ok' | 'err' };
const ToastCtx = createContext<(msg: string, kind?: 'ok' | 'err') => void>(() => {});

export const useToast = () => useContext(ToastCtx);

export function ToastHost({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback((msg: string, kind: 'ok' | 'err' = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((v) => [...v, { id, msg, kind }]);
    setTimeout(() => setItems((v) => v.filter((t) => t.id !== id)), 3200);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        style={{
          position: 'absolute', bottom: 'calc(var(--safe-b) + 92px)', left: 16, right: 16,
          zIndex: 90, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none',
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            style={{
              padding: '13px 15px', borderRadius: 13,
              background: t.kind === 'ok' ? 'var(--ink)' : 'var(--err)',
              color: '#fff', fontSize: 13.5, fontWeight: 600,
              boxShadow: '0 8px 26px rgba(11,18,32,.28)',
              animation: 'alUp .24s cubic-bezier(.22,.9,.3,1)',
            }}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** Squelette de chargement — jamais un écran blanc. */
export function Spinner({ label }: { label?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} aria-label={label || 'Chargement'}>
      <div style={{ height: 86, borderRadius: 16, background: 'var(--sk)', animation: 'alSk 1.2s infinite' }} />
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1, height: 86, borderRadius: 16, background: 'var(--sk)', animation: 'alSk 1.2s infinite .1s' }} />
        <div style={{ flex: 1, height: 86, borderRadius: 16, background: 'var(--sk)', animation: 'alSk 1.2s infinite .15s' }} />
      </div>
      <div style={{ height: 160, borderRadius: 16, background: 'var(--sk)', animation: 'alSk 1.2s infinite .2s' }} />
    </div>
  );
}

/** Bandeau d'erreur d'API — le message vient du serveur, déjà lisible. */
export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      style={{
        border: '1px solid rgba(220,38,38,.35)', borderRadius: 14, background: 'rgba(220,38,38,.07)',
        padding: '13px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <i className="ri-error-warning-line" style={{ fontSize: 17, color: 'var(--err)', marginTop: 1, flex: 'none' }}></i>
      <span style={{ flex: 1, fontSize: 12, color: '#991b1b', lineHeight: 1.45 }}>{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{ border: 'none', background: 'transparent', color: 'var(--err)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44 }}
        >
          Réessayer
        </button>
      )}
    </div>
  );
}

/** Horloge relancée chaque seconde — pour le temps écoulé d'un pointage. */
export function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}
